import type { BaseSyntheticEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { LanguageValue } from '@elfsight/configurator-contracts';
import type {
  PaymentSuccessHandler,
  ProductData,
  StripeIntegrationSettings
} from '@elfsight/lib-stripe-integration';
import {
  ActionButton,
  CheckoutFormWrapper,
  getSubmitPaymentData,
  isPaymentSessionStatus,
  isSuccessfulSessionStatusData,
  PaymentErrorMessage,
  pollSessionStatus,
  processSuccessfulPaymentInfo,
  Section,
  SecurePaymentCaption,
  StripeFormLoadingContainer,
  usePaymentSessionMetadata
} from '@elfsight/lib-stripe-integration';
import { Show, useWidgetTokenStore } from '@elfsight/lib-utils';
import { PaymentElement, useCheckout } from '@stripe/react-stripe-js';
import { useSettings } from '@wu/widget-core';
import { noop } from 'es-toolkit';
import { CheckoutItem } from '../blocks';
import {
  useCheckoutColors,
  useCheckoutFormSubmit,
  useCheckoutI18n,
  useCheckoutNavigation
} from '../providers';
import { useCheckoutNarrowness } from '../utils';

export type StripeCheckoutElementsProps = {
  onPaymentSuccess: PaymentSuccessHandler;
  productData: ProductData;
  onReady?: () => void;
};

export function StripeCheckoutElements({
  onPaymentSuccess,
  productData,
  onReady = noop
}: StripeCheckoutElementsProps) {
  const { textColor, accentColor, errorColor } = useCheckoutColors();
  const { withForm, setCheckoutStep } = useCheckoutNavigation();
  const { submit } = useCheckoutFormSubmit();
  const { confirm } = useCheckout();

  const {
    integrationStripe: { cancellationPolicyText },
    language
  } = useSettings<StripeIntegrationSettings & { language: LanguageValue }>();
  const { language: locale = window.navigator.language } = language ?? {};

  const widgetTokenStore = useWidgetTokenStore();
  const widgetToken = widgetTokenStore.get();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const {
    sessionId,
    setSessionStatus,
    isPaymentInProcess,
    setIsPaymentInProcess
  } = usePaymentSessionMetadata();
  const { t } = useCheckoutI18n();
  const isNarrow = useCheckoutNarrowness();

  const cancelPollRef = useRef<AbortController>();

  const handlePay = () => {
    setError(undefined);

    if (isPaymentInProcess) {
      setError(t('Payment is already in process'));

      return;
    }

    if (!sessionId) {
      setError(t('Payment session id is undefined'));
      setIsPaymentInProcess(false);

      return;
    }

    setIsPaymentInProcess(true);
    setSessionStatus('open');

    confirm({ redirect: 'if_required' })
      .then((result) => {
        if (result.type === 'error') {
          throw new Error(result.error.message);
        }

        cancelPollRef.current = new AbortController();

        return pollSessionStatus({
          sessionId,
          abortSignal: cancelPollRef.current.signal,
          widgetToken,
          validateStatus: ({ status }) => {
            const isKnownStatus = isPaymentSessionStatus(status);

            if (isKnownStatus) {
              setSessionStatus(status);
            }

            // when returns true - polling will stop
            return status !== 'open';
          }
        });
      })
      .then(async (statusPayload) => {
        if (isSuccessfulSessionStatusData(statusPayload)) {
          await onPaymentSuccess(statusPayload);

          if (withForm) {
            const processedPaymentInfo = processSuccessfulPaymentInfo({
              sessionStatusData: statusPayload,
              productData,
              locale,
              translatedSuccessStatus: t('Payment Received')
            });

            const submitData = getSubmitPaymentData(processedPaymentInfo);

            if (!submitData) {
              throw new Error('Received invalid payment info');
            }

            await submit?.({} as BaseSyntheticEvent, {
              payment: { ...submitData, cancellationPolicyText }
            });
          }

          setCheckoutStep('success');

          return;
        }

        const { status, failureCode, failureMessage, paymentStatus } =
          statusPayload;

        if (!isPaymentSessionStatus(status)) {
          throw new Error(`${t('Unknown payment session status')}: ${status}`);
        }

        const failureError = failureMessage || failureCode;

        if (failureError) {
          throw new Error(failureError);
        }

        if (paymentStatus !== 'paid') {
          throw new Error(t('Payment failed'));
        }

        if (status !== 'complete') {
          throw new Error(t('Payment session was expired or canceled'));
        }

        throw new Error(t('Something went wrong'));
      })
      .catch((error) => {
        setSessionStatus('error');

        if (error instanceof Error) {
          setError(error.message);
        }
      })
      .finally(() => {
        setIsPaymentInProcess(false);
      });
  };

  useEffect(() => {
    onReady();

    return () => cancelPollRef.current?.abort();
  }, [onReady]);

  return (
    <CheckoutFormWrapper $color={textColor} $disabled={isPaymentInProcess}>
      <StripeFormLoadingContainer
        isLoading={isLoading}
        tabsGap={12}
        withCondensedFields
      >
        <PaymentElement
          options={{
            paymentMethodOrder: ['card'],
            layout: 'tabs',
            readOnly: isPaymentInProcess
          }}
          onReady={() => setIsLoading(false)}
        />
      </StripeFormLoadingContainer>

      <Show when={isNarrow}>
        <Section>
          <CheckoutItem />
        </Section>
      </Show>

      <Section>
        <ActionButton
          actionText={t('Pay Now')}
          disabledText={t('Processing Payment...')}
          onClick={handlePay}
          disabled={isPaymentInProcess}
          color={accentColor}
          stretch
          maintainAppearanceWhenDisabled
        />

        <Show when={!!error}>
          <PaymentErrorMessage color={errorColor}>{error}</PaymentErrorMessage>
        </Show>

        <SecurePaymentCaption
          color={textColor}
          translatedTextLabel={t('Secure payments are processed via')}
        />
      </Section>
    </CheckoutFormWrapper>
  );
}
